import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, useNavigate } from "react-router";
import { Routes } from '@datadog/browser-rum-react/react-router-v7';
import ResultsPage from "./results-page/results-page";
import AnalysisPage from "./analysis-page";
import { makeStyles } from "@mui/styles";
import { fetchAnalysisLoadingState, selectDocumentId, selectExecutionId, selectDocumentSettings, selectLoadingPreviousAnalysisStatus, selectSuggestions } from "../../selectors/suggestions";
import { selectExperimentId, selectIsRunning } from "../../selectors/experiments";
import { selectUseMockResponseState } from "../../selectors/featureFlags";
import { getDocumentPropertyNameFromSetting, loadPreviousAnalysis, setAnalyzeReportStatus, setDocumentId, setExecutionId } from "../../redux/suggestions";
import { useAnalyzeReport } from "../../hooks/useAnalyzeReport";
import useDocumentIdFetcher from "../../hooks/useDocumentIdFetcher";
import { AppDispatch } from "../../store";
import { DocumentSettingsType } from "../../models/documentSettings";
import { AnalysisMessageData, MessageReceivedEvent } from "../../models/webSockets";
import SocketService from "../../services/socket/service";
import ExactReportHeader from "../ExactReportHeader";
import ExactReportFooter from "../ExactReportFooter";
import { FetchLoadingStateEnum, staticChannelName } from "../../constants";
import { setDocumentSetting } from "./api";
import { useSuggestionStatusHandler } from "../../hooks/useSuggestionStatusHandler";

const useStyles = makeStyles(() => ({
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  content: {
    overflowY: 'auto',
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'column',
    display: 'flex',
  }
}));

const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { startAnalyzeReport, analysisMessageHandler } = useAnalyzeReport();
  const { suggestionStatusMessageHandler } = useSuggestionStatusHandler();
  const executionId = useSelector(selectExecutionId);
  const newAnalysisLoadingState = useSelector(fetchAnalysisLoadingState);
  const useMockResponse = useSelector(selectUseMockResponseState);
  const suggestions = useSelector(selectSuggestions);
  const loadingPreviousAnalysisStatus = useSelector(selectLoadingPreviousAnalysisStatus);
  const documentId = useSelector(selectDocumentId);
  const [socketService, setSocketService] = useState(null);
  const { getDocumentId } = useDocumentIdFetcher();

  const documentSettings = useSelector(selectDocumentSettings);
  const experimentId = useSelector(selectExperimentId);
  const isRunningExperiment = useSelector(selectIsRunning);
  const isExperimentWorkflowActive = experimentId != null;
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function extractAndStoreWordDocumentFileId() {
      const documentId = await getDocumentId();
      dispatch(setDocumentId(documentId));
    }
    extractAndStoreWordDocumentFileId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!documentId) return;

    const channelName = `${staticChannelName}/${documentId}`;
    if (channelName) {
      const socket = SocketService.getInstance(channelName);
      socket.subscribeListener<AnalysisMessageData>(analysisMessageHandler);
      socket.subscribeListener(suggestionStatusMessageHandler);
      setSocketService(socket);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  useEffect(() => {
    if (loadingPreviousAnalysisStatus === FetchLoadingStateEnum.fulfilled && documentId.length > 0 && (!isExperimentWorkflowActive && !isRunningExperiment)) {
      navigate(suggestions?.length ? '/results' : '/analysis');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPreviousAnalysisStatus, documentId, isExperimentWorkflowActive, isRunningExperiment]);

  useEffect(() => {
    const handleNavigation = async () => {
      if (newAnalysisLoadingState === FetchLoadingStateEnum.fulfilled && documentId.length > 0 && (!isExperimentWorkflowActive && !isRunningExperiment)) {
        await setDocumentSetting('isLoading', null);
        navigate('/results');
      }
    };
    handleNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAnalysisLoadingState, documentId, isExperimentWorkflowActive, isRunningExperiment]);

  useEffect(() => {
    dispatch(loadPreviousAnalysis());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchDocumentSettings() {
      await dispatch(getDocumentPropertyNameFromSetting(DocumentSettingsType.isLoading));
    }
    fetchDocumentSettings();
  }, [dispatch, newAnalysisLoadingState]);

  useEffect(() => {
    if (isExperimentWorkflowActive || isRunningExperiment) return;
    if (documentSettings.isLoading === 'true') {
      navigate('/analysis');
    }
  }, [documentSettings.isLoading, isExperimentWorkflowActive, isRunningExperiment, navigate, dispatch]);

  const startAIAnalysis = async ({ jurisdictionId }: { jurisdictionId?: string }) => {
    const newExecutionId = crypto.randomUUID();
    await dispatch(setExecutionId(newExecutionId));
    socketService.broadcastMessage({
      event: MessageReceivedEvent.ANALYSIS_START,
      data: {
        itpExecutionId: newExecutionId,
      },
    });
    await setDocumentSetting('isLoading', true);
    dispatch(setAnalyzeReportStatus(FetchLoadingStateEnum.loading));
    await startAnalyzeReport({ useMockResponse, itpExecutionId: newExecutionId, jurisdictionId });
    navigate('/analysis');
  };

  return (
    <div className={classes.container}>
      <ExactReportHeader refreshAnalysis={startAIAnalysis} scrollableContainerRef={scrollableContainerRef} />
      <div className={classes.content} ref={scrollableContainerRef}>
        <Routes>
          <Route path="/analysis" element={<AnalysisPage startAIAnalysis={startAIAnalysis} documentSettings={documentSettings} />} />
          <Route path="/results" element={<ResultsPage />} />
        </Routes>
        <ExactReportFooter executionId={executionId} />
      </div>
    </div>
  );
};

export default HomePage;